import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import cx from 'classnames';

import { useCrossFlow } from '@atlassiansox/cross-flow-support/trello';
import type {
  ActionSubjectIdType,
  EventAttribute,
} from '@trello/atlassian-analytics';
import { Analytics } from '@trello/atlassian-analytics';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { useFeatureFlag } from '@trello/feature-flag-client';
import type { featureFlags } from '@trello/feature-flag-client/data';
import { forTemplate } from '@trello/i18n';
import { Button } from '@trello/nachos/button';
import { CloseIcon } from '@trello/nachos/icons/close';
// import { isCurrentRoute, routes } from '@trello/router';
import { DiscoveryAdsIds } from '@trello/test-ids';
import { useGlobalTheme } from '@trello/theme';

import { CloseButton } from 'app/src/components/CloseButton';
import type { TemplateCategory } from './constants';
import { AD_VARIATIONS, FEEDBACK_OPTIONS } from './constants';

import styles from './DiscoveryAdPowerUp.less';

// eslint-disable-next-line @trello/assets-alongside-implementation
import thankYouImage from 'resources/images/discovery-ads/thank-you.svg';

const format = forTemplate('discovery_ads');

export enum Step {
  Ad = 'Ad',
  Feedback = 'Feedback',
  Thanks = 'Thanks',
  Dismissed = 'Dismissed',
}

export enum Source {
  PowerUpPopupMenu = 'pluginMenuScreen',
  PowerUpDirectory = 'boardDirectory',
}

export interface DiscoveryAdPowerUpProps {
  source: Source;
  category?: TemplateCategory;
  containers?: Parameters<
    typeof Analytics.sendClickedButtonEvent
  >[0]['containers'];
}

const getAdVariation = (source: Source, category: TemplateCategory) => {
  // TODO: update this with variations based on location/content
  if (source === Source.PowerUpPopupMenu) {
    return 'powerup_menu';
  }
  return 'powerup_directory';
};

const getAttributes = (source: Source, category: TemplateCategory) => {
  // TODO: determine if we need additional attributes for analytics
  const attributes: EventAttribute = {};
  return attributes;
};

// Placeholder feature flags
// TODO: Replace the placeholder ffs with the actual ffs
const getFlags = (source: Source): (keyof typeof featureFlags)[] => {
  switch (source) {
    case Source.PowerUpPopupMenu:
    case Source.PowerUpDirectory:
    default:
      return ['1p-ads-view-switcher-personal', '1p-ads-view-switcher-business'];
  }
};

export const DiscoveryAdPowerUp: FunctionComponent<DiscoveryAdPowerUpProps> = ({
  source,
  category,
  containers,
}) => {
  const [componentStep, setComponentStep] = useState<Step>(Step.Ad);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();

  const { dismissOneTimeMessage, isOneTimeMessageDismissed } =
    useOneTimeMessagesDismissed();

  const { effectiveColorMode } = useGlobalTheme();

  const isDarkMode = effectiveColorMode === 'dark';

  const shouldShowCffe = useFeatureFlag('xf.cffe-for-1p-ads', false);
  const crossflow = useCrossFlow();

  const isCffeEnabled = shouldShowCffe && crossflow.isEnabled;

  const {
    image_light,
    image_dark,
    content,
    href,
    css_container,
    dismiss_message,
  } = AD_VARIATIONS[getAdVariation(source, category)];

  const [, flag2] = getFlags(source);
  // const firstFlag =
  //   useFeatureFlag<'control' | 'not-enrolled' | 'experiment'>(
  //     flag1,
  //     'not-enrolled',
  //     {
  //       sendExposureEvent: true,
  //     },
  //   ) === 'experiment';
  const firstFlag = true;

  const secondFlag =
    useFeatureFlag<'control' | 'not-enrolled' | 'experiment'>(
      flag2,
      'not-enrolled',
      {
        sendExposureEvent: true,
      },
    ) === 'experiment';

  const attributes = getAttributes(source, category);

  useEffect(() => {
    let actionSubjectId: ActionSubjectIdType = 'jiraWorkManagementDiscoveryAd';

    // We want to ensure the displayed event doesn't fire if our flag is off
    if (firstFlag || secondFlag) {
      switch (componentStep) {
        case Step.Feedback:
          actionSubjectId = 'jiraWorkManagementDiscoveryAdFeedback';
          break;
        case Step.Thanks:
          actionSubjectId = 'jiraWorkManagementDiscoveryAdThankYou';
          break;
        default:
          break;
      }

      if (componentStep !== Step.Dismissed) {
        Analytics.sendUIEvent({
          action: 'displayed',
          actionSubject: 'discoveryAd',
          actionSubjectId,
          source,
          attributes: getAttributes(source, category),
          containers: containers || {},
        });
      }
    }
  }, [componentStep, source, category, containers, firstFlag, secondFlag]);

  // We want to ensure the ad does not show up if there is a
  // oneTimeMessageDismissed, however we only want to check for this
  // on the initial load of an ad, since the flow of our feedback form
  // necessitates the oneTimeMessage being sent without immediate dismissal
  useEffect(() => {
    if (
      componentStep === Step.Ad &&
      isOneTimeMessageDismissed(dismiss_message)
    ) {
      setComponentStep(Step.Dismissed);
    }
  }, [isOneTimeMessageDismissed, dismiss_message, componentStep]);

  const handleAdClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (isCffeEnabled) {
        e.preventDefault();
        crossflow.api.open({
          targetProduct: 'jira-core.ondemand',
          journey: 'get-started',
          sourceComponent: 'template-category',
          sourceContext: 'get-started',
          experimentalOptions: {
            isTrello1PAd: true,
            trelloWorkspaceId:
              containers?.workspace?.id || containers?.organization?.id || null,
          },
        });
      }

      Analytics.sendClickedButtonEvent({
        buttonName: 'jiraWorkManagementDiscoveryAdLearnMoreButton',
        source,
        attributes,
        containers: containers || {},
      });
    },
    // eslint-disable-next-line @trello/exhaustive-deps
    [crossflow, source, attributes, containers],
  );

  const handleDismissClick = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'dismissJiraWorkManagementDiscoveryAdButton',
      source,
      attributes,
      containers: containers || {},
    });
    dismissOneTimeMessage(dismiss_message);
    setComponentStep(Step.Feedback);
  }, [
    setComponentStep,
    dismissOneTimeMessage,
    dismiss_message,
    attributes,
    source,
    containers,
  ]);

  const handleFeedbackChoice = useCallback(
    (buttonName: ActionSubjectIdType) => {
      Analytics.sendClickedButtonEvent({
        buttonName,
        source,
        attributes,
        containers: containers || {},
      });
      setComponentStep(Step.Thanks);
      setTimeoutId(
        setTimeout(() => {
          setComponentStep(Step.Dismissed);
        }, 1000),
      );
    },
    [attributes, source, containers],
  );

  const handleFeedbackDismiss = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'dismissJiraWorkManagementDiscoveryAdFeedbackButton',
      source,
      attributes,
      containers: containers || {},
    });
    setComponentStep(Step.Dismissed);
  }, [attributes, source, containers]);

  const handleThankYouDismiss = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'dismissJiraWorkManagementDiscoveryAdThankYouButton',
      source,
      attributes,
      containers: containers || {},
    });
    setComponentStep(Step.Dismissed);
    clearTimeout(timeoutId);
  }, [timeoutId, attributes, source, containers]);

  const Ad = (
    <div
      className={cx(css_container, styles.container, styles.adContainer)}
      data-testid={DiscoveryAdsIds.AdContainer}
    >
      <div className={styles.adImageContainer}>
        <img
          src={isDarkMode ? image_dark : image_light}
          alt={format('ad-image-alt')}
          role="presentation"
        />
        <CloseButton
          closeIcon={<CloseIcon />}
          className={styles.closeButton}
          onClick={handleDismissClick}
          type="button"
          // We don't want this color to change based on theme, so we are using a hard-coded color and not a token
          // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
          color="white"
          large={true}
          testId={DiscoveryAdsIds.DismissButton}
        />
      </div>
      <div className={styles.adContentContainer}>
        <div className={styles.adText}>
          <p>{format(content)}</p>
        </div>
        <a
          className={styles.buttonLink}
          href={href}
          role={'button'}
          onClick={handleAdClick}
          target="_blank"
          aria-label={
            isCffeEnabled ? 'get-started-alt' : format('learn-more-alt')
          }
        >
          {isCffeEnabled ? 'Get Started' : format('learn-more')}
        </a>
      </div>
    </div>
  );

  const FeedbackForm = (
    <div
      className={cx(css_container, styles.container, styles.feedbackContainer)}
      data-testid={DiscoveryAdsIds.FeedbackContainer}
    >
      <div className={styles.feedbackHeader}>
        <div className={styles.feedbackTitle}>{format('feedback')}</div>
        <div className={styles.feedbackCloseButton}>
          <CloseButton
            closeIcon={<CloseIcon />}
            className={styles.closeButton}
            onClick={handleFeedbackDismiss}
            type="button"
            large={false}
            testId={DiscoveryAdsIds.DismissButton}
          />
        </div>
      </div>
      <div className={styles.feedbackOptionsContainer}>
        {FEEDBACK_OPTIONS.map(({ value, name }) => (
          <Button
            key={value}
            // eslint-disable-next-line react/jsx-no-bind
            onClick={() => {
              handleFeedbackChoice(value);
            }}
            className={styles.feedbackButton}
          >
            {format(name)}
          </Button>
        ))}
      </div>
    </div>
  );

  const ThankYou = (
    <div
      className={cx(css_container, styles.container, styles.thankYouContainer)}
      data-testid={DiscoveryAdsIds.ThankYouContainer}
    >
      <div className={styles.closeButton}>
        <CloseButton
          closeIcon={<CloseIcon />}
          className={styles.closeButton}
          onClick={handleThankYouDismiss}
          type="button"
          large={true}
          testId={DiscoveryAdsIds.DismissButton}
        />
      </div>
      <div className={styles.thankYouContentContainer}>
        <img src={thankYouImage} alt="" role="presentation" />
        <p>{format('thank-you')}</p>
      </div>
    </div>
  );

  if (firstFlag || secondFlag) {
    switch (componentStep) {
      case Step.Ad:
        return Ad;
      case Step.Feedback:
        return FeedbackForm;
      case Step.Thanks:
        return ThankYou;
      // default captures Step.Dismissed
      default:
        return null;
    }
  } else {
    return null;
    // NOTE: Can uncomment "return ad" to show ad
    // return Ad;
  }
};
